/* FastCandy home banner — standalone behaviour.
   Independent of the Pokémon bundle by design; nothing here reads or writes
   anything outside this section.

   What it does:
     • stacked-deck crossfade between slides (the outgoing slide is held fully
       opaque underneath while the incoming fades in, so the background never
       shows through mid-transition)
     • dots / arrows / keyboard navigation
     • autoplay whose countdown RESTARTS on every manual navigation, so a slide
       the visitor picked always gets its full viewing time
     • horizontal touch swipe, decided on touchend by dominant axis — fully
       passive, nothing calls preventDefault, so vertical page scrolling over
       the banner is always native
     • product tilt driven by ONE calculation for mouse and finger alike
       (normalised to the product box), reset cleanly when the pointer leaves,
       lifts, or the browser claims the gesture for scrolling
   Respects prefers-reduced-motion: no autoplay, no tilt. */
(function () {
  "use strict";

  var reduced = function () {
    return window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  };

  function initBanner(root) {
    if (root._fcWired) return;
    root._fcWired = true;

    var track = root.querySelector(".fc-slides__track");
    var slides = root.querySelectorAll(".fc-slide");
    var dots = root.querySelectorAll("[data-fc-slide-dot]");
    if (!slides.length) return;

    // `cur` is the logical slide — every control navigates from it, never from
    // a measured position, so rapid clicks land exactly where they should.
    var cur = 0;
    var restartAuto = function () {};   // replaced below when autoplay is on

    function apply(i) {
      var next = ((i % slides.length) + slides.length) % slides.length;
      if (next !== cur && slides[cur]) {
        var leaving = slides[cur];
        leaving.classList.add("fc-slide--leaving");
        if (leaving._fcLeaveT) clearTimeout(leaving._fcLeaveT);
        leaving._fcLeaveT = setTimeout(function () {
          leaving.classList.remove("fc-slide--leaving");
        }, 1300);
      }
      cur = next;
      Array.prototype.forEach.call(dots, function (d, di) {
        d.setAttribute("aria-selected", di === cur ? "true" : "false");
      });
      Array.prototype.forEach.call(slides, function (sl, si) {
        sl.classList.toggle("fc-slide--active", si === cur);
        if (si === cur) {
          sl.classList.remove("fc-slide--leaving");
          if (sl._fcLeaveT) { clearTimeout(sl._fcLeaveT); sl._fcLeaveT = null; }
        }
      });
    }

    function goTo(i) { apply(i); restartAuto(); }

    /* ---- controls ------------------------------------------------------- */
    Array.prototype.forEach.call(dots, function (d) {
      d.addEventListener("click", function () {
        goTo(parseInt(d.getAttribute("data-fc-slide-dot"), 10) || 0);
      });
    });
    var prev = root.querySelector("[data-fc-slide-prev]");
    var next = root.querySelector("[data-fc-slide-next]");
    if (prev) prev.addEventListener("click", function () { goTo(cur - 1); });
    if (next) next.addEventListener("click", function () { goTo(cur + 1); });
    if (track) {
      track.addEventListener("keydown", function (e) {
        if (e.key === "ArrowLeft") { e.preventDefault(); goTo(cur - 1); }
        if (e.key === "ArrowRight") { e.preventDefault(); goTo(cur + 1); }
      });
    }

    /* ---- touch swipe (passive; the page keeps its native scrolling) ------ */
    if (slides.length > 1) {
      var sx = 0, sy = 0, tracking = false;
      root.addEventListener("touchstart", function (e) {
        if (e.touches.length !== 1) { tracking = false; return; }
        sx = e.touches[0].clientX; sy = e.touches[0].clientY; tracking = true;
      }, { passive: true });
      root.addEventListener("touchend", function (e) {
        if (!tracking || !e.changedTouches || !e.changedTouches[0]) return;
        tracking = false;
        var dx = e.changedTouches[0].clientX - sx;
        var dy = e.changedTouches[0].clientY - sy;
        // clearly horizontal only: a dead zone plus a dominance ratio, so a
        // vertical scroll or a small diagonal never flips the slide
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) {
          goTo(cur + (dx < 0 ? 1 : -1));
        }
      }, { passive: true });
      root.addEventListener("touchcancel", function () { tracking = false; }, { passive: true });
    }

    /* ---- autoplay: ONE timer, restarted by every manual navigation ------- */
    var autoS = parseFloat(root.getAttribute("data-fc-autoplay"));
    if (autoS > 0 && slides.length > 1 && !reduced()) {
      var timer = null, hoverPaused = false;
      var stop = function () { if (timer) { clearInterval(timer); timer = null; } };
      var start = function () {
        stop();
        timer = setInterval(function () {
          if (document.hidden || hoverPaused) return;
          apply(cur + 1);            // apply, not goTo — no self-restart
        }, autoS * 1000);
      };
      restartAuto = function () { if (!hoverPaused) start(); };
      start();
      // NO pause on hover: a full-width hero sits under the customer's resting
      // pointer most of the time, and "pause on hover" froze the deck on
      // whatever slide it happened to show (reported as "stops on the last
      // slide"). The deck loops forever; only a hidden tab pauses it.
      document.addEventListener("visibilitychange", function () {
        if (document.hidden) stop(); else if (!hoverPaused) start();
      });
    }

    apply(0);
  }

  /* ---- product tilt: one engine, any pointer --------------------------- */
  function initTilt(root) {
    Array.prototype.forEach.call(root.querySelectorAll(".fc-zoombanner__product[data-fc-hover]"), function (prod) {
      if (prod._fcTilt || reduced()) return;
      prod._fcTilt = true;
      var tilt = prod.querySelector(".fc-zoombanner__prodtilt");
      if (!tilt) return;

      // normalised to the product's own box, so it feels the same on a phone
      // and on a wide desktop
      var applyTilt = function (e) {
        var r = prod.getBoundingClientRect();
        if (!r.width || !r.height) return;
        var nx = (e.clientX - r.left) / r.width - 0.5;
        var ny = (e.clientY - r.top) / r.height - 0.5;
        tilt.style.setProperty("--hx", (nx * 12).toFixed(2) + "deg");
        tilt.style.setProperty("--hy", (-ny * 9).toFixed(2) + "deg");
        tilt.style.setProperty("--htx", (nx * 10).toFixed(1) + "px");
        tilt.style.setProperty("--hty", (ny * 8).toFixed(1) + "px");
      };
      var reset = function () {
        tilt.style.setProperty("--hx", "0deg");
        tilt.style.setProperty("--hy", "0deg");
        tilt.style.setProperty("--htx", "0px");
        tilt.style.setProperty("--hty", "0px");
      };

      prod.addEventListener("pointermove", applyTilt);
      // A finger's FIRST event is pointerdown, and the browser may claim the
      // gesture for scrolling before any move arrives — so touch responds to
      // the press itself. Passive: scrolling is never blocked.
      prod.addEventListener("pointerdown", function (e) {
        if (e.pointerType !== "mouse") applyTilt(e);
      }, { passive: true });
      prod.addEventListener("pointerleave", reset);
      prod.addEventListener("pointerup", reset);
      prod.addEventListener("pointercancel", reset);
    });
  }

  function initAll(scope) {
    var root = scope || document;
    Array.prototype.forEach.call(root.querySelectorAll("[data-fc-slideshow]"), function (el) {
      initBanner(el);
      initTilt(el);
    });
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () { initAll(); });
  } else {
    initAll();
  }
  // Shopify theme editor: a section re-render replaces the DOM
  document.addEventListener("shopify:section:load", function (e) { initAll(e.target); });
})();
